import {
  Plus,
  Database,
  FileText,
  Eye,
  Braces,
  GitFork,
  Download,
} from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useModalStore } from '@/stores/modal-store';
import type { Command } from '../types';

/**
 * Database-scoped commands. Guards lean on `activeConnectionId` +
 * `activeTab` to figure out which database is in scope. A focused
 * info/table/query/designer/code-editor tab carries the database field;
 * we use it, or fall back to the connection's default.
 */

function currentDbContext(ctx: import('../types').CommandContext) {
  const t = ctx.activeTab as { connectionId?: string; database?: string; schema?: string } | null;
  if (!t?.connectionId) return null;
  return {
    connectionId: t.connectionId,
    database: t.database,
    schema: t.schema ?? 'public',
  };
}

export const databaseCommands: Command[] = [
  {
    id: 'db.create',
    label: 'Create Database...',
    icon: Plus,
    category: 'Database',
    keywords: ['new'],
    when: (ctx) => ctx.activeConnectionId !== null,
    run: (ctx) => {
      if (!ctx.activeConnectionId) return;
      useModalStore.getState().openCreateDatabase(ctx.activeConnectionId);
    },
  },
  {
    id: 'db.new-table',
    label: 'New Table (Designer)...',
    icon: FileText,
    category: 'Database',
    keywords: ['create', 'designer'],
    when: (ctx) => currentDbContext(ctx) !== null,
    run: (ctx) => {
      const d = currentDbContext(ctx);
      if (!d) return;
      useWorkspaceStore.getState().openTab({
        type: 'designer',
        id: `designer:${d.connectionId}:${d.schema}:new:${Date.now()}`,
        connectionId: d.connectionId,
        database: d.database,
        schema: d.schema,
        mode: 'create',
      });
    },
  },
  {
    id: 'db.new-view',
    label: 'New View...',
    icon: Eye,
    category: 'Database',
    keywords: ['create'],
    when: (ctx) => currentDbContext(ctx) !== null,
    run: (ctx) => {
      const d = currentDbContext(ctx);
      if (!d) return;
      useWorkspaceStore.getState().openTab({
        type: 'code-editor',
        id: `code-editor:${d.connectionId}:${d.schema}:new-view:${Date.now()}`,
        connectionId: d.connectionId,
        database: d.database,
        schema: d.schema,
        objectName: 'new_view',
        objectType: 'view',
        mode: 'create',
      });
    },
  },
  {
    id: 'db.new-function',
    label: 'New Function...',
    icon: Braces,
    category: 'Database',
    keywords: ['create', 'procedure'],
    when: (ctx) => currentDbContext(ctx) !== null,
    run: (ctx) => {
      const d = currentDbContext(ctx);
      if (!d) return;
      useWorkspaceStore.getState().openTab({
        type: 'code-editor',
        id: `code-editor:${d.connectionId}:${d.schema}:new-func:${Date.now()}`,
        connectionId: d.connectionId,
        database: d.database,
        schema: d.schema,
        objectName: 'new_function',
        objectType: 'function',
        mode: 'create',
      });
    },
  },
  {
    id: 'db.view-erd',
    label: 'View Schema ERD',
    icon: GitFork,
    category: 'Database',
    keywords: ['diagram', 'graph', 'relationships'],
    when: (ctx) => currentDbContext(ctx) !== null,
    run: (ctx) => {
      const d = currentDbContext(ctx);
      if (!d) return;
      useWorkspaceStore.getState().openTab({
        type: 'erd',
        id: `erd:${d.connectionId}:${d.database ?? 'default'}`,
        connectionId: d.connectionId,
        databaseName: d.database ?? 'default',
      });
    },
  },
  {
    id: 'db.info',
    label: 'Show Database Info',
    icon: Database,
    category: 'Database',
    keywords: ['stats', 'size'],
    when: (ctx) => currentDbContext(ctx) !== null,
    run: (ctx) => {
      const d = currentDbContext(ctx);
      if (!d) return;
      useWorkspaceStore.getState().openTab({
        type: 'info',
        id: `info:${d.connectionId}:${d.database ?? 'default'}`,
        connectionId: d.connectionId,
        database: d.database,
        objectType: 'database',
        objectName: d.database ?? 'default',
      });
    },
  },
  {
    id: 'db.backup',
    label: 'Backup Database...',
    icon: Download,
    category: 'Database',
    keywords: ['dump', 'export', 'sql'],
    when: (ctx) => currentDbContext(ctx) !== null,
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:db:backup'));
    },
  },
];
